import * as url from "../helpers/url_helper";
import { buildDcMessages } from "./db/dcMessages";
import { MESSAGE_CATEGORIES } from "../helpers/messageContract";
import { NOTIFY_CATEGORY_BY_TAB } from "../constants/notifyCategories";

/**
 * DcMessage mock（notify-api 票 03–06）—— 網址、參數、回傳形狀與真後端一致（「mock 跟 API 一模一樣」）。
 *
 * 契約:swagger 9/29 ＋ 9/30 SIT smoke(`.scratch/notify-api/contract-smoke-20260930.md`)。
 * - `targetCustomerId` 真的篩;**不帶或 0＝全部人的訊息**(實測如此,不是 mock 寬鬆)
 * - 分頁信封 `{ total_count, total_pages, has_previous_page, has_next_page, items, page_index, page_size }`
 * - 新的在前(sent_on 由新到舊)
 *
 * 獨立成檔(同 approvals.js):mock/index.js 已近三千行,訊息這組端點自成一塊。
 * `withAuth`／`toPageEnvelope` 由 index 傳進來,401 與分頁信封全站同一份。
 */
let store = [];

// GetCategoryCounts 的 value／text(9/30 實測 GetCategories:1–6,順序同 DcMessageCategory enum)。
// 從既有常數推出來(value↔enum 在 messageContract、分頁 key↔中文在 notifyCategories),不另寫一份對照表
const CATEGORY_COUNT_ROWS = MESSAGE_CATEGORIES.map((c) => ({
  value: c.value, enumName: c.enumName, text: NOTIFY_CATEGORY_BY_TAB[c.tab],
}));

/** 測試用:目前 store 的快照(斷言寫入後的狀態) */
export const dcMessageStore = () => store;

const timeOf = (m) => new Date(m.sent_on || m.created_on).getTime() || 0;

/** 依 GetAll 的 query 篩出訊息(GetCategoryCounts／GetUnreadCount 也共用這一份語意) */
export function filterMessages(rows, p = {}) {
  let out = rows;
  const target = Number(p.targetCustomerId) || 0;
  if (target) out = out.filter((m) => m.target_customer_id === target);
  if (p.isRead !== undefined && p.isRead !== null && p.isRead !== "") {
    const want = String(p.isRead) === "true";
    out = out.filter((m) => m.is_read === want);
  }
  if (p.messageCategory) out = out.filter((m) => m.message_category === p.messageCategory);
  if (p.status) out = out.filter((m) => m.status === p.status);
  if (p.category) out = out.filter((m) => m.category === p.category);
  const kw = String(p.keyword || "").trim().toLowerCase();
  if (kw) {
    // ⚠️ swagger 沒寫 keyword 比對哪些欄位;先比使用者看得到的字(內文／標題／分類名稱／單號),
    // 實測後以後端為準
    out = out.filter((m) => [m.content, m.title, m.module_name, m.related_request_no]
      .some((v) => String(v ?? "").toLowerCase().includes(kw)));
  }
  return out;
}

export default function registerDcMessage(mock, { withAuth, toPageEnvelope }) {
  store = buildDcMessages();
  const base = url.DCK_BACKEND.MESSAGE;

  mock.onGet(`${base}/GetAll`).reply(withAuth((config) => {
    const p = config.params || {};
    const rows = filterMessages(store, p).slice().sort((a, b) => timeOf(b) - timeOf(a));
    const pageIndex = Number(p.pageIndex) || 0;
    const pageSize = Number(p.pageSize) || 20;
    return [200, toPageEnvelope(rows, pageIndex, pageSize)];
  }));

  // 形狀以實測為準(swagger 沒寫型別):[{ value:1..6, text, count }],六類固定六列(0 筆也回);
  // 種類不在六類的訊息(SIT 的 0)不算進任何一列 —— 與 9/30 實測「2984 則、六類都是 0」一致
  mock.onGet(`${base}/GetCategoryCounts`).reply(withAuth((config) => {
    const p = config.params || {};
    const rows = filterMessages(store, { targetCustomerId: p.targetCustomerId, isRead: p.isRead });
    return [200, CATEGORY_COUNT_ROWS.map(({ value, enumName, text }) => ({
      value, text, count: rows.filter((m) => m.message_category === enumName).length,
    }))];
  }));

  // targetCustomerId 必填(swagger required)—— 不帶就 400,跟 ASP.NET 綁定失敗一樣;回整數
  mock.onGet(`${base}/GetUnreadCount`).reply(withAuth((config) => {
    const p = config.params || {};
    if (p.targetCustomerId === undefined || p.targetCustomerId === null || p.targetCustomerId === "") {
      return [400, { title: "One or more validation errors occurred.", errors: { targetCustomerId: ["The targetCustomerId field is required."] } }];
    }
    return [200, filterMessages(store, { targetCustomerId: p.targetCustomerId, isRead: false }).length];
  }));

  // 寫入兩支都是 POST **query** 參數、無 body(swagger)—— 參數從網址讀,body 有東西也不看
  const queryOf = (config) => {
    const qs = (config.url.split("?")[1] || "");
    return { ...Object.fromEntries(new URLSearchParams(qs)), ...(config.params || {}) };
  };
  const touch = (m) => { m.is_read = true; m.updated_on = new Date().toISOString(); };

  mock.onPost(new RegExp(`${base}/MarkAsRead`)).reply(withAuth((config) => {
    const id = Number(queryOf(config).id);
    const m = store.find((x) => x.id === id);
    if (!m) return [404, { title: "Not Found", status: 404 }];
    touch(m); // 已讀再標一次也是 200(冪等)
    return [200];
  }));

  // 回清掉的筆數。不帶 targetCustomerId 會清掉**全部人的**未讀 —— 與 GetAll 同語意,前端一律帶自己
  mock.onPost(new RegExp(`${base}/MarkAllAsRead`)).reply(withAuth((config) => {
    const q = queryOf(config);
    const rows = filterMessages(store, { targetCustomerId: q.targetCustomerId, messageCategory: q.messageCategory, isRead: false });
    rows.forEach(touch);
    return [200, rows.length];
  }));
}
